import React, { useContext, useMemo, useState, useEffect } from "react";
import { useRouter } from "next/router";

import { Box, Divider } from "@bca/ui-library";
import { categoriesWithSearchInput, enabledCategories } from "./filtersConfig";
import { CollapsibleFacet } from "./CollapsibleFacet";
import { debounce } from "../utils/debounce";
import { ErrorContext } from "../../src/context/ErrorContext";
import { FacetType } from "./filters.type";
import { useCurrentFacet } from "./useCurrentFacet.hook";
import { useRangeFacet } from "./useRangeFacet.hook";

import AppliedFilters from "./AppliedFilters";
import CAPCleanPriceFilter from "./CAPCleanPriceFilter";
import CheckboxGroup from "./CheckboxGroup";
import CollapsibleSkeleton from "./CollapsibleSkeleton";
import ColourFacet from "./ColourFacet";
import CustomInput from "./CustomInput";
import GradeFacet from "./GradeFacet";
import LogbookStatusFacet from "./LogbookStatusFacet";
import MileageFilter from "./MileageFilter";
import PlateFacet from "./PlateFacet";
import useFacetList from "./useFacetList.hook";
import VehicleAgeFilter from "./VehicleAgeFilter";
import { TRACKED } from "../constants";
import BatteryGradeFacet from "./BatteryGradeFacet";
import { useFeatureFlags } from "bca-feature-flag-provider";
import { FacetValues } from "../StockAlerts/types";

const getIsDisabled = (categoryKey, allQueryStrings) => {
  if (
    categoryKey !== "ModelGroup" &&
    categoryKey !== "Model" &&
    categoryKey !== "Derivative" &&
    categoryKey !== "HybridType"
  ) {
    return (
      !enabledCategories.multiselect.includes(categoryKey) &&
      !enabledCategories.range.includes(categoryKey)
    );
  }

  const bq = allQueryStrings["bq"] || "";

  const hybridTypeEnableCondition =
    /electric/i.test(bq) && /(petrol|diesel)/i.test(bq);

  return !Boolean(
    bq.includes("Make") || bq.includes("make") || hybridTypeEnableCondition
  );
};

const FilterCategories: React.FC<{
  setSelectedVehicles: React.Dispatch<React.SetStateAction<any[]>>;
  appliedCheckboxes: any;
  setAppliedCheckboxes: any;
  searchResultsLoading: boolean;
}> = ({
  setSelectedVehicles,
  appliedCheckboxes,
  setAppliedCheckboxes,
  searchResultsLoading,
}) => {
  const errorContext = useContext(ErrorContext);
  const [currentFacet, setCurrentFacet] = useState("VehicleType"); // default Facet to load
  const {
    featureFlags: { phev: phevFlag },
  } = useFeatureFlags();

  const [query, setQuery] = useState("");
  const [, setRefreshCollapsible] = useState();

  const router = useRouter();
  const { query: allQueryStrings } = router;
  const { requestOrigin } = allQueryStrings;

  const isTrackedPage = requestOrigin === TRACKED;

  useEffect(() => {
    if (query !== "") {
      setQuery("");
    }
  }, [currentFacet]);

  const { facetListResult, facetListError } = useFacetList(isTrackedPage);

  const {
    facet: currentFacetResponse,
    facetError: subFiltersFacetCountsError,
  } = useCurrentFacet(allQueryStrings, currentFacet as FacetType);

  const subFilters = currentFacetResponse?.counts;
  const additionalInformation = currentFacetResponse?.additionalInformation;

  const {
    facetCounts: rangeSubfilters,
    facetCountsError: rangeSubFiltersFacetCountsError,
  } = useRangeFacet(allQueryStrings, currentFacet as FacetType);

  if (
    facetListError ||
    subFiltersFacetCountsError ||
    rangeSubFiltersFacetCountsError
  ) {
    errorContext?.dispatch({ type: "filters" });
  }

  let filteredValues = [];
  if (query?.length > 0) {
    subFilters?.forEach(({ filterKey, filterValues }) => {
      const filtered = filterValues.filter(({ name }) => {
        const nameLowerCase = name.toLowerCase();
        const queryLowerCase = query.toLowerCase();
        return (
          nameLowerCase.includes(queryLowerCase) &&
          (nameLowerCase[nameLowerCase.indexOf(queryLowerCase) - 1] === " " ||
            nameLowerCase[nameLowerCase.indexOf(queryLowerCase) - 1] ===
              undefined)
        );
      });

      if (filtered?.length > 0) {
        filteredValues.push({ filterKey, filterValues: [...filtered] });
      }
    });
  }

  let filteredSubFilters = query?.length > 0 ? filteredValues : subFilters;

  const changeHandler = (event) => {
    setQuery(event.target.value);
  };

  const debouncedInputChangeHandler = useMemo(
    () => debounce(changeHandler, 200),
    []
  );

  const isInErrorState = () => {
    return (
      errorContext?.state.somethingWentWrong &&
      errorContext?.state.type === "filters"
    );
  };

  return (
    <>
      {!isInErrorState() && (
        <Box mt={5} pb="120px" width="269px">
          {!facetListResult && (
            <CollapsibleSkeleton isTrackedPage={isTrackedPage} />
          )}
          {facetListResult?.map(
            ({ displayLabel, key: categoryKey, parentFilterKey }, index) => {
              if (categoryKey === FacetValues.HYBRID_TYPE && !phevFlag) {
                return null;
              }
              return (
                <Box key={`${categoryKey}-${index}`}>
                  <CollapsibleFacet
                    categoryKey={categoryKey}
                    setCurrentFacet={setCurrentFacet}
                    currentFacet={currentFacet}
                    title={displayLabel}
                    isDisabled={getIsDisabled(categoryKey, allQueryStrings)}
                    setQuery={setQuery}
                    chipsSlot={
                      <AppliedFilters
                        filterByCategoryKey={categoryKey}
                        appliedCheckboxes={appliedCheckboxes}
                        setAppliedCheckboxes={setAppliedCheckboxes}
                        setSelectedVehicles={setSelectedVehicles}
                        mb={5}
                        allQueryStrings={allQueryStrings}
                      />
                    }
                  >
                    {categoriesWithSearchInput.find(
                      (item) => item.name === categoryKey
                    ) && (
                      <CustomInput
                        error={false}
                        placeholder={
                          categoriesWithSearchInput.find(
                            (item) => item.name === categoryKey
                          )?.text
                        }
                        onChange={debouncedInputChangeHandler}
                      />
                    )}
                    {enabledCategories.multiselect.includes(currentFacet) &&
                      currentFacet !== "Grade" &&
                      currentFacet !== "LogbookStatus" &&
                      currentFacet !== "HasBatteryHealthReport" &&
                      currentFacet !== "Plate" &&
                      currentFacet !== "ColourGeneric" && (
                        <CheckboxGroup
                          additionalInformation={additionalInformation}
                          filteredSubfilters={filteredSubFilters}
                          categoryKey={currentFacet}
                          setSelectedVehicles={setSelectedVehicles}
                          setRefreshCollapsible={setRefreshCollapsible}
                          setAppliedCheckboxes={setAppliedCheckboxes}
                          appliedCheckboxes={appliedCheckboxes}
                          parentFilterKey={parentFilterKey}
                        />
                      )}
                    {enabledCategories.multiselect.includes(currentFacet) &&
                      currentFacet === "Grade" && (
                        <GradeFacet
                          filteredSubfilters={filteredSubFilters}
                          setSelectedVehicles={setSelectedVehicles}
                          setAppliedCheckboxes={setAppliedCheckboxes}
                          appliedCheckboxes={appliedCheckboxes}
                        />
                      )}
                    {enabledCategories.multiselect.includes(currentFacet) &&
                      currentFacet === "ColourGeneric" && (
                        <ColourFacet
                          filteredSubfilters={filteredSubFilters}
                          setSelectedVehicles={setSelectedVehicles}
                          setAppliedCheckboxes={setAppliedCheckboxes}
                          appliedCheckboxes={appliedCheckboxes}
                        />
                      )}
                    {currentFacet === "Mileage" && (
                      <MileageFilter
                        setSelectedVehicles={setSelectedVehicles}
                        setAppliedCheckboxes={setAppliedCheckboxes}
                        appliedCheckboxes={appliedCheckboxes}
                        rangeSubfilters={rangeSubfilters}
                        searchResultsLoading={searchResultsLoading}
                      />
                    )}
                    {currentFacet === "CapCleanPrice" && (
                      <CAPCleanPriceFilter
                        setSelectedVehicles={setSelectedVehicles}
                        setAppliedCheckboxes={setAppliedCheckboxes}
                        appliedCheckboxes={appliedCheckboxes}
                        rangeSubfilters={rangeSubfilters}
                        searchResultsLoading={searchResultsLoading}
                      />
                    )}
                    {currentFacet === "DateRegistered" && (
                      <VehicleAgeFilter
                        setSelectedVehicles={setSelectedVehicles}
                        setAppliedCheckboxes={setAppliedCheckboxes}
                        appliedCheckboxes={appliedCheckboxes}
                        rangeSubfilters={rangeSubfilters}
                        searchResultsLoading={searchResultsLoading}
                      />
                    )}
                    {enabledCategories.multiselect.includes(currentFacet) &&
                      currentFacet === "LogbookStatus" && (
                        <LogbookStatusFacet
                          filteredSubfilters={filteredSubFilters}
                          setSelectedVehicles={setSelectedVehicles}
                          setAppliedCheckboxes={setAppliedCheckboxes}
                          appliedCheckboxes={appliedCheckboxes}
                        />
                      )}
                    {enabledCategories.multiselect.includes(currentFacet) &&
                      currentFacet === "HasBatteryHealthReport" && (
                        <BatteryGradeFacet
                          filteredSubfilters={filteredSubFilters}
                          setSelectedVehicles={setSelectedVehicles}
                          setAppliedCheckboxes={setAppliedCheckboxes}
                          appliedCheckboxes={appliedCheckboxes}
                        />
                      )}
                    {enabledCategories.multiselect.includes(currentFacet) &&
                      currentFacet === "Plate" && (
                        <PlateFacet
                          filteredSubfilters={filteredSubFilters}
                          setSelectedVehicles={setSelectedVehicles}
                          setAppliedCheckboxes={setAppliedCheckboxes}
                          appliedCheckboxes={appliedCheckboxes}
                        />
                      )}
                  </CollapsibleFacet>
                  {(categoryKey === "Derivative" ||
                    categoryKey === "SaleChannel") && <Divider />}
                </Box>
              );
            }
          )}
        </Box>
      )}
    </>
  );
};

export default FilterCategories;
